import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScanFace, ScanSearch, Brush, Scissors } from 'lucide-react';
import SalonHeaderActions from '../components/SalonHeaderActions';
import { salonAppPath, SALON_HUB_PATH } from '../config/salonApp';
import { hairAppPath } from '../../hair-analyzer/src/hair/config/hairApp';
import {
  BEAUTY_PRODUCTS,
  expandEntitledBeautyModules,
  isBeautyModuleEntitled,
  SAAS_MODULE_IDS,
} from '../../beauty-shared/config/saasModules';
import './FlowSelectPage.css';

const STORAGE_KEY = 'salon_entitled_beauty_modules';

function readEntitledModules() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
}

const BASE_OPTIONS = [
  {
    id: 'salon',
    title: 'Salon Skin Analysis',
    description:
      'Run the full salon diagnostics flow — capture, AI skin analysis, routines, products, treatments, and booking.',
    icon: ScanFace,
    path: salonAppPath('/landing'),
    cta: 'Continue Salon Flow',
    always: true,
  },
  {
    id: 'makeup',
    moduleId: BEAUTY_PRODUCTS.makeup.moduleId,
    title: BEAUTY_PRODUCTS.makeup.title,
    description: BEAUTY_PRODUCTS.makeup.description,
    icon: Brush,
    path: BEAUTY_PRODUCTS.makeup.appBase,
    cta: BEAUTY_PRODUCTS.makeup.cta,
  },
  {
    id: 'hair-style',
    moduleId: [
      BEAUTY_PRODUCTS.hairColoring.moduleId,
      BEAUTY_PRODUCTS.hairCut.moduleId,
    ],
    title: 'Hair Style',
    description:
      'Try hair colour and hair styles in one studio — shades, intensity, cuts, and live preview.',
    icon: Scissors,
    path: BEAUTY_PRODUCTS.hairColoring.appBase,
    cta: 'Open Hair Style',
  },
  {
    id: 'hair-analyze',
    moduleId: SAAS_MODULE_IDS.HAIR_ANALYZE,
    title: 'Hair Analyze',
    description:
      'AI-powered hair and scalp analysis with health insights, damage detection, and treatment recommendations.',
    icon: ScanSearch,
    path: hairAppPath('/'),
    cta: 'Start Hair Analysis',
    // Hair analyze currently ships open on hub (same as prior enablement).
    always: true,
  },
];

export default function FlowSelectPage() {
  const navigate = useNavigate();
  const [entitledRaw] = useState(() => readEntitledModules());

  const flowOptions = useMemo(() => {
    const entitledSet = entitledRaw
      ? expandEntitledBeautyModules(entitledRaw)
      : null;
    return BASE_OPTIONS.filter((option) => {
      if (option.always) return true;
      return isBeautyModuleEntitled(entitledSet, option.moduleId);
    });
  }, [entitledRaw]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const mq = window.matchMedia('(max-width: 1024px)');

    const clearScrollMode = () => {
      html.classList.remove('ny-salon-hub-scroll');
      body.classList.remove('ny-salon-hub-scroll');
    };

    const applyScrollMode = () => {
      if (mq.matches) {
        html.classList.add('ny-salon-hub-scroll');
        body.classList.add('ny-salon-hub-scroll');
      } else {
        clearScrollMode();
      }
    };

    applyScrollMode();
    mq.addEventListener('change', applyScrollMode);

    return () => {
      mq.removeEventListener('change', applyScrollMode);
      clearScrollMode();
    };
  }, []);

  return (
    <div className="ny-salon-hub">
      <header className="ny-salon-hub__header">
        <SalonHeaderActions
          homeButtonClassName="ny-salon-hub__icon-btn"
          logoutButtonClassName="ny-salon-hub__icon-btn"
          onHome={() => navigate(SALON_HUB_PATH)}
        />
      </header>

      <main className="ny-salon-hub__main">
        <p className="ny-salon-hub__kicker">Welcome back</p>
        <h1 className="ny-salon-hub__title">Choose how you want to continue</h1>
        <p className="ny-salon-hub__subtitle">
          Your salon login works across all experiences. Pick one to get started.
        </p>

        <div className="ny-salon-hub__grid" role="list">
          {flowOptions.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.id}
                type="button"
                className="ny-salon-hub__card"
                onClick={() => {
                  if (option.path) navigate(option.path);
                }}
              >
                <span className="ny-salon-hub__card-icon" aria-hidden="true">
                  <Icon size={28} strokeWidth={1.8} />
                </span>
                <span className="ny-salon-hub__card-copy">
                  <span className="ny-salon-hub__card-title">{option.title}</span>
                  <span className="ny-salon-hub__card-desc">{option.description}</span>
                </span>
                <span className="ny-salon-hub__card-cta">{option.cta}</span>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
